import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Loader2, Heart, LogIn, UserPlus, KeyRound, ArrowLeft, CheckCircle, Shield, Users } from "lucide-react";
import { getSecurityQuestions, requestPasswordReset, verifyAndResetPassword, verify2FA } from "@/lib/api";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_OPTIONS = [
  { value: "patient", label: "Patient", description: "I am receiving hospice care" },
  { value: "caregiver", label: "Family Caregiver", description: "I am caring for a loved one" },
  { value: "staff", label: "Staff Member", description: "I work for Goodwin Hospice" },
  { value: "admin", label: "Administrator", description: "I am a Goodwin Hospice administrator" },
];

export default function AuthModal({ open, onOpenChange }) {
  const { login, register, complete2FA } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("login");
  const [resetStep, setResetStep] = useState(0); // 0: email, 1: security question, 2: success
  const [securityQuestions, setSecurityQuestions] = useState([]);
  const [show2FA, setShow2FA] = useState(false);
  const [twoFACode, setTwoFACode] = useState("");
  const [pendingUser, setPendingUser] = useState(null);
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [registerData, setRegisterData] = useState({ 
    email: "", 
    password: "", 
    name: "",
    role: "caregiver",
    securityQuestion: "",
    securityAnswer: ""
  });
  const [resetData, setResetData] = useState({
    email: "",
    securityQuestion: "",
    securityAnswer: "",
    newPassword: "",
    confirmPassword: ""
  });

  useEffect(() => {
    loadSecurityQuestions();
  }, []);

  const loadSecurityQuestions = async () => {
    try {
      const data = await getSecurityQuestions();
      setSecurityQuestions(data.questions || []);
    } catch (error) {
      console.error("Failed to load security questions:", error);
    }
  };

  const fetchDashboardRedirect = async (token) => {
    try {
      const response = await fetch(`${API_URL}/api/rbac/dashboard-redirect`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        return data.route || '/dashboard';
      }
    } catch (error) {
      console.error("Failed to fetch dashboard redirect:", error);
    }
    return '/dashboard';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await login(loginData.email, loginData.password);
      
      if (response.requires_2fa) {
        setPendingUser(response);
        setShow2FA(true);
        toast.info("Please enter your 2FA code");
      } else {
        toast.success("Welcome back!");
        onOpenChange(false);
        setLoginData({ email: "", password: "" });
        
        // Fetch role-based dashboard redirect
        const redirectRoute = await fetchDashboardRedirect(response.token);
        navigate(redirectRoute);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handle2FAVerify = async () => {
    if (twoFACode.length !== 6 && twoFACode.length !== 8) {
      toast.error("Please enter a valid code");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await verify2FA(twoFACode);
      localStorage.setItem("hospice_token", response.token);
      complete2FA(pendingUser);
      toast.success("Welcome back!");
      onOpenChange(false);
      setLoginData({ email: "", password: "" });
      setTwoFACode("");
      setShow2FA(false);
      setPendingUser(null);
      
      // Fetch role-based dashboard redirect
      const redirectRoute = await fetchDashboardRedirect(response.token);
      navigate(redirectRoute);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (registerData.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setIsLoading(true);
    try {
      const response = await register(
        registerData.email, 
        registerData.password, 
        registerData.name, 
        registerData.role,
        registerData.securityQuestion || null,
        registerData.securityAnswer || null
      );
      toast.success("Account created successfully!");
      onOpenChange(false);
      setRegisterData({ email: "", password: "", name: "", role: "caregiver", securityQuestion: "", securityAnswer: "" });
      
      // Fetch role-based dashboard redirect
      const redirectRoute = await fetchDashboardRedirect(response.token);
      navigate(redirectRoute);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await requestPasswordReset(resetData.email);
      if (response.has_security_question) {
        setResetData(prev => ({ ...prev, securityQuestion: response.security_question }));
        setResetStep(1);
      } else {
        toast.error("No security question set for this account. Please contact support.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate password reset");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyReset = async (e) => {
    e.preventDefault();
    if (resetData.newPassword !== resetData.confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    if (resetData.newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setIsLoading(true);
    try {
      await verifyAndResetPassword(
        resetData.email,
        resetData.securityAnswer,
        resetData.newPassword
      );
      setResetStep(2);
      toast.success("Password reset successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setIsLoading(false);
    }
  };

  const resetToLogin = () => {
    setActiveTab("login");
    setResetStep(0);
    setResetData({ email: "", securityQuestion: "", securityAnswer: "", newPassword: "", confirmPassword: "" });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="auth-modal">
        <DialogHeader className="text-center">
          <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
            <Heart className="w-6 h-6 text-primary" />
          </div>
          <DialogTitle className="font-serif text-2xl">Welcome to Goodwin Cares</DialogTitle>
          <DialogDescription>
            Sign in to save conversations, access your journal, and message your care team
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="login" data-testid="login-tab">
              <LogIn className="w-4 h-4 mr-1" />
              Sign In
            </TabsTrigger>
            <TabsTrigger value="register" data-testid="register-tab">
              <UserPlus className="w-4 h-4 mr-1" />
              Register
            </TabsTrigger>
            <TabsTrigger value="reset" data-testid="reset-tab">
              <KeyRound className="w-4 h-4 mr-1" />
              Reset
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            {!show2FA ? (
              <form onSubmit={handleLogin} className="space-y-4 mt-4" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="login-email">Email</Label>
                  <Input
                    id="login-email"
                    type="email"
                    placeholder="your@email.com"
                    value={loginData.email}
                    onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                    required
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="login-password">Password</Label>
                  <Input
                    id="login-password"
                    type="password"
                    placeholder="••••••••"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    required
                    data-testid="login-password-input"
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" disabled={isLoading} data-testid="login-submit-btn">
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
                  Sign In
                </Button>
                <button
                  type="button"
                  onClick={() => setActiveTab("reset")}
                  className="w-full text-sm text-primary hover:underline"
                >
                  Forgot password?
                </button>
              </form>
            ) : (
              <div className="space-y-4 mt-4" data-testid="2fa-form">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Enter the 6-digit code from your authenticator app
                  </p>
                </div>
                <div className="space-y-2">
                  <Input
                    type="text"
                    inputMode="numeric"
                    maxLength={8}
                    placeholder="000000"
                    value={twoFACode}
                    onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ''))}
                    className="text-center text-2xl tracking-[0.3em] font-mono"
                    autoFocus
                    data-testid="2fa-code-input"
                  />
                  <p className="text-xs text-center text-muted-foreground">
                    Or use a backup code if you lost access to your app
                  </p>
                </div>
                <Button 
                  className="w-full rounded-full" 
                  onClick={handle2FAVerify}
                  disabled={isLoading || (twoFACode.length !== 6 && twoFACode.length !== 8)}
                  data-testid="2fa-verify-btn"
                >
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle className="w-4 h-4 mr-2" />}
                  Verify
                </Button>
                <button
                  type="button"
                  onClick={() => {
                    setShow2FA(false);
                    setTwoFACode("");
                    setPendingUser(null);
                  }}
                  className="w-full text-sm text-muted-foreground hover:text-foreground flex items-center justify-center"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to login
                </button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4 mt-4" data-testid="register-form">
              <div className="space-y-2">
                <Label htmlFor="register-name">Full Name</Label>
                <Input
                  id="register-name"
                  placeholder="Your name"
                  value={registerData.name}
                  onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
                  required
                  data-testid="register-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-email">Email</Label>
                <Input
                  id="register-email"
                  type="email"
                  placeholder="your@email.com"
                  value={registerData.email}
                  onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-password">Password</Label>
                <Input
                  id="register-password"
                  type="password"
                  placeholder="••••••••"
                  value={registerData.password}
                  onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
              </div>

              {/* Role Selection */}
              <div className="space-y-2">
                <Label htmlFor="register-role" className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-primary" />
                  I am a...
                </Label>
                <Select
                  value={registerData.role}
                  onValueChange={(value) => setRegisterData({ ...registerData, role: value })}
                >
                  <SelectTrigger id="register-role" data-testid="register-role-select">
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map((role) => (
                      <SelectItem key={role.value} value={role.value}>
                        <div className="flex flex-col">
                          <span className="font-medium">{role.label}</span>
                          <span className="text-xs text-muted-foreground">{role.description}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {(registerData.role === "staff" || registerData.role === "admin") && (
                  <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded-lg">
                    Note: Staff/Admin accounts require verification. Portal access will be granted after approval.
                  </p>
                )}
              </div>
              
              <div className="border-t pt-4 mt-4">
                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Password Recovery (Recommended)</span>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="security-question">Security Question</Label>
                  <Select
                    value={registerData.securityQuestion}
                    onValueChange={(value) => setRegisterData({ ...registerData, securityQuestion: value })}
                  >
                    <SelectTrigger id="security-question">
                      <SelectValue placeholder="Select a security question" />
                    </SelectTrigger>
                    <SelectContent>
                      {securityQuestions.map((q, i) => (
                        <SelectItem key={i} value={q}>{q}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {registerData.securityQuestion && (
                  <div className="space-y-2 mt-2">
                    <Label htmlFor="security-answer">Your Answer</Label>
                    <Input
                      id="security-answer"
                      placeholder="Your answer"
                      value={registerData.securityAnswer}
                      onChange={(e) => setRegisterData({ ...registerData, securityAnswer: e.target.value })}
                    />
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full rounded-full" disabled={isLoading} data-testid="register-submit-btn">
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
                Create Account
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="reset">
            {resetStep === 0 && (
              <form onSubmit={handleRequestReset} className="space-y-4 mt-4">
                <p className="text-sm text-muted-foreground">
                  Enter your email address and we'll help you reset your password.
                </p>
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="your@email.com"
                    value={resetData.email}
                    onChange={(e) => setResetData({ ...resetData, email: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" disabled={isLoading}>
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Continue
                </Button>
              </form>
            )}

            {resetStep === 1 && (
              <form onSubmit={handleVerifyReset} className="space-y-4 mt-4">
                <button
                  type="button"
                  onClick={() => setResetStep(0)}
                  className="flex items-center text-sm text-muted-foreground hover:text-foreground"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back
                </button>
                <div className="p-3 bg-primary/5 rounded-lg border border-primary/20">
                  <p className="text-sm font-medium text-primary">{resetData.securityQuestion}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="reset-answer">Your Answer</Label>
                  <Input
                    id="reset-answer"
                    placeholder="Enter your answer"
                    value={resetData.securityAnswer}
                    onChange={(e) => setResetData({ ...resetData, securityAnswer: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={resetData.newPassword}
                    onChange={(e) => setResetData({ ...resetData, newPassword: e.target.value })}
                    required
                    minLength={6}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={resetData.confirmPassword}
                    onChange={(e) => setResetData({ ...resetData, confirmPassword: e.target.value })}
                    required
                  />
                </div>
                <Button type="submit" className="w-full rounded-full" disabled={isLoading}>
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <KeyRound className="w-4 h-4 mr-2" />}
                  Reset Password
                </Button>
              </form>
            )}

            {resetStep === 2 && (
              <div className="text-center space-y-4 py-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-medium">Password Reset Successfully!</h3>
                <p className="text-sm text-muted-foreground">
                  You can now sign in with your new password.
                </p>
                <Button onClick={resetToLogin} className="rounded-full">
                  <LogIn className="w-4 h-4 mr-2" />
                  Sign In Now
                </Button>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
